import React, { useState, useEffect, KeyboardEvent } from "react";
import { photoLike } from "../services/photoService";
import { FaHeart, FaRegHeart } from "react-icons/fa";

interface LikeButtonProps {
  photoId: number;
  initialLiked: boolean;
  initialLikeCount: number;
  size?: string;
  iconSize?: string;
  customTextColor?: string;
  onToggle?: (liked: boolean, likeCount: number) => void;
  onLikeAction?: (id: number) => Promise<{ liked: boolean; likeCount: number }>;
}

const LikeButton: React.FC<LikeButtonProps> = ({
  photoId,
  initialLiked,
  initialLikeCount,
  size = "text-base",
  iconSize = "w-5 h-5",
  customTextColor = "text-accentAlt hover:text-red-500",
  onToggle,
  onLikeAction,
}) => {
  const [liked, setLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(initialLikeCount);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLiked(initialLiked);
    setLikeCount(initialLikeCount);
  }, [initialLiked, initialLikeCount]);

  const toggleLike = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const result = onLikeAction
        ? await onLikeAction(photoId)
        : await photoLike(photoId);

      setLiked(result.liked);
      setLikeCount(result.likeCount);
      onToggle?.(result.liked, result.likeCount);
    } catch {
      alert("Failed to toggle like.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (loading) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleLike();
    }
  };

  const Icon = liked ? FaHeart : FaRegHeart;
  const iconClasses = `${iconSize} transition-colors duration-200 ${
    liked ? "text-red-500" : customTextColor
  }`;

  return (
    <div
      role="button"
      aria-pressed={liked}
      aria-disabled={loading}
      tabIndex={loading ? -1 : 0}
      onClick={(e) => {
        e.stopPropagation();
        toggleLike();
      }}
      onKeyDown={handleKeyDown}
      className={`
        flex items-center space-x-1 ${size}
        ${loading ? "opacity-50 pointer-events-none" : "cursor-pointer"}
      `}
    >
      <Icon className={iconClasses} />
      <span className={customTextColor}>{likeCount}</span>
    </div>
  );
};

export default LikeButton;
